import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Activity, Building2, RefreshCw, Search, UserCheck, Users } from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import { getAdminCpList, getAdminCpTerminals } from '../api/cp';

const rowsOf = (data) => data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);

function normalizeCp(row = {}, index = 0) {
  return {
    raw: row,
    id: row.id ?? row.cpPkId ?? row.cp_pk_id ?? row.cp_id ?? index,
    cpId: row.cpId ?? row.cp_id ?? '-',
    name: row.name ?? '-',
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
    updatedAt: row.updatedAt ?? row.updated_at ?? row.createdAt ?? row.created_at ?? '',
  };
}

function normalizeTerminal(row = {}, cp = {}, index = 0) {
  return {
    id: `terminal-${cp.id}-${row.id ?? row.terminalPkId ?? row.terminal_pk_id ?? row.terminal_id ?? index}`,
    targetId: row.terminalId ?? row.loginId ?? row.login_id ?? row.terminal_id ?? '-',
    name: row.name ?? row.terminalName ?? row.terminal_name ?? '-',
    type: 'TERMINAL',
    owner: cp.name,
    ownerId: cp.cpId,
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
    updatedAt: row.updatedAt ?? row.updated_at ?? row.createdAt ?? row.created_at ?? '',
  };
}

function statusLabel(status) {
  if (status === 'ACTIVE') return '활성';
  if (status === 'INACTIVE') return '비활성';
  if (status === 'SUSPENDED') return '정지';
  return status || '-';
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY.MM.DD HH:mm:ss') : '-';
}

function Summary({ icon: Icon, label, value, accent = false }) {
  return <div className="flex items-center gap-3 rounded-xl border border-[#e5e8eb] bg-white px-4 py-3"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent ? 'bg-amber-50 text-amber-600' : 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}><Icon size={18} /></span><div><p className="text-[11px] text-[#8b95a1]">{label}</p><strong className="mt-0.5 block text-[19px] font-extrabold text-[#191f28]">{value}</strong></div></div>;
}

export default function StatusLog() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [checkedAt, setCheckedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cpData = await getAdminCpList({ page: 1, limit: 200 });
      const cps = rowsOf(cpData).map(normalizeCp);
      const terminalGroups = await Promise.all(cps.map(async (cp) => {
        try {
          const terminalData = await getAdminCpTerminals(cp.id, { page: 1, limit: 500 });
          return rowsOf(terminalData).map((row, index) => normalizeTerminal(row, cp, index));
        } catch (_) {
          return [];
        }
      }));
      const cpRows = cps.map((cp) => ({ id: `cp-${cp.id}`, targetId: cp.cpId, name: cp.name, type: 'CP', owner: '-', ownerId: '-', status: cp.status, updatedAt: cp.updatedAt }));
      setRows([...cpRows, ...terminalGroups.flat()]);
      setCheckedAt(new Date());
    } catch (error) {
      setRows([]);
      showToast(error?.message || '상태 정보를 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return rows.filter((row) => {
      const typeMatched = type === 'ALL' || row.type === type;
      const statusMatched = status === 'ALL' || row.status === status;
      const keywordMatched = !keyword || [row.targetId, row.name, row.owner, row.ownerId].some((value) => String(value || '').toLowerCase().includes(keyword));
      return typeMatched && statusMatched && keywordMatched;
    });
  }, [query, rows, status, type]);

  const cpCount = rows.filter((row) => row.type === 'CP').length;
  const terminalCount = rows.filter((row) => row.type === 'TERMINAL').length;
  const inactiveCount = rows.filter((row) => row.status !== 'ACTIVE').length;

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-[#f6f9f7] px-5 py-5 lg:px-7">
      <div className="mx-auto flex w-full max-w-[1540px] flex-col gap-4">
        <header className="flex items-end justify-between gap-4">
          <div><h1 className="text-[23px] font-extrabold text-[#191f28]">가맹점·영업자 상태</h1><p className="mt-1 text-[12px] text-[#6b7684]">현재 계정 상태를 한 번에 확인합니다. 이력 API 연결 전에는 최신 상태만 표시됩니다.</p></div>
          <div className="flex items-center gap-2 text-[11px] text-[#8b95a1]">{checkedAt && <span>{dayjs(checkedAt).format('YYYY.MM.DD HH:mm:ss')} 확인</span>}<button type="button" onClick={load} disabled={loading} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#dfe3e8] bg-white px-3 font-semibold text-[#4e5968] hover:bg-[#f9fafb] disabled:opacity-50"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} />새로고침</button></div>
        </header>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Summary icon={Building2} label="가맹점" value={`${cpCount.toLocaleString('ko-KR')}곳`} />
          <Summary icon={Users} label="영업자" value={`${terminalCount.toLocaleString('ko-KR')}명`} />
          <Summary icon={Activity} label="확인 필요" value={`${inactiveCount.toLocaleString('ko-KR')}건`} accent={inactiveCount > 0} />
        </section>

        <section className="overflow-hidden rounded-xl border border-[#dfe5e1] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eef1f4] px-4 py-3">
            <div className="flex items-center gap-2"><UserCheck size={17} className="text-[var(--brand-primary)]" /><h2 className="text-[15px] font-bold text-[#191f28]">현재 상태 목록</h2><span className="text-[11px] text-[#8b95a1]">총 {filtered.length.toLocaleString('ko-KR')}건</span></div>
            <div className="flex items-center gap-2">
              <label className="flex h-9 w-[280px] items-center gap-2 rounded-lg border border-[#dfe3e8] px-3 focus-within:border-[var(--brand-primary)]"><Search size={15} className="text-[#8b95a1]" /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="이름, ID, 소속 검색" className="min-w-0 flex-1 border-0 bg-transparent text-[12px] outline-none" /></label>
              <select value={type} onChange={(event) => setType(event.target.value)} className="h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px] text-[#4e5968] outline-none"><option value="ALL">전체 구분</option><option value="CP">가맹점</option><option value="TERMINAL">영업자</option></select>
              <select value={status} onChange={(event) => setStatus(event.target.value)} className="h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px] text-[#4e5968] outline-none"><option value="ALL">전체 상태</option><option value="ACTIVE">활성</option><option value="INACTIVE">비활성</option><option value="SUSPENDED">정지</option></select>
            </div>
          </div>
          <div className="max-h-[calc(100dvh-310px)] min-h-[360px] overflow-auto">
            <table className="w-full table-fixed text-left text-[12px]">
              <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]"><tr className="h-10 border-b border-[#e5e8eb]"><th className="w-[10%] px-4">구분</th><th className="w-[19%] px-4">이름</th><th className="w-[18%] px-4">계정 ID</th><th className="w-[22%] px-4">소속 가맹점</th><th className="w-[12%] px-4 text-center">상태</th><th className="w-[19%] px-4">최종 갱신</th></tr></thead>
              <tbody className="divide-y divide-[#f0f2f1]">
                {loading ? <tr><td colSpan={6} className="py-20 text-center text-[#8b95a1]">상태 정보를 불러오는 중입니다.</td></tr> : filtered.length === 0 ? <tr><td colSpan={6} className="py-20 text-center text-[#8b95a1]">조건에 맞는 상태 정보가 없습니다.</td></tr> : filtered.map((row) => <tr key={row.id} className="h-[52px] hover:bg-[#f9fafb]"><td className="px-4"><span className={`rounded px-2 py-1 text-[10px] font-bold ${row.type === 'CP' ? 'bg-blue-50 text-blue-700' : 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}>{row.type === 'CP' ? '가맹점' : '영업자'}</span></td><td className="truncate px-4 font-bold text-[#191f28]">{row.name}</td><td className="truncate px-4 font-mono text-[#4e5968]">{row.targetId}</td><td className="truncate px-4 text-[#4e5968]">{row.type === 'CP' ? '-' : `${row.owner} (${row.ownerId})`}</td><td className="px-4 text-center"><span className={`rounded px-2 py-1 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : row.status === 'SUSPENDED' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{statusLabel(row.status)}</span></td><td className="px-4 text-[#8b95a1]">{formatDateTime(row.updatedAt)}</td></tr>)}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
